// CLINICAL CALENDAR v2 (2026-09-30, Chad). The calendar stays familiar - a week grid with time down the left.
// The intelligence is in what it tells the clinician: the workload strip, who still needs an individual
// session, what is expiring, and suggested clients when an open time is clicked.
// Groups come from the EMR group schedule (read-only here). Overlaps warn, never block.
// A clinician sees only their own calendar; supervisors can open anyone's at the facility.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { T } from './theme';

const API = 'https://tcio-api.onrender.com';
const START_H = 7, END_H = 21, HOUR_PX = 48;
const KINDS = [
  ['individual', 'Individual session'], ['family', 'Family session'], ['case_mgmt', 'Case management'],
  ['assessment', 'Assessment'], ['supervision', 'Supervision'], ['meeting', 'Meeting'],
  ['admin', 'Admin time'], ['out_of_office', 'Out of office'], ['other', 'Other'],
];
const KIND_LABEL = Object.fromEntries(KINDS);
const SHORT = { individual: 'Individual', family: 'Family', case_mgmt: 'Case mgmt', assessment: 'Assessment', supervision: 'Supervision',
  meeting: 'Meeting', admin: 'Admin time', out_of_office: 'Out of office', other: 'Other', group: 'Group' };
const CLIENT_KINDS = ['individual', 'family', 'case_mgmt', 'assessment'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Quiet colour coding: blue individual, teal group, purple assessment, gray admin / unavailable.
const TONE = {
  blue: { bg: '#EAF3FE', bar: '#0878E8', ink: '#0B5CB8' },
  teal: { bg: '#E3F5F2', bar: '#12A594', ink: '#0B7A6E' },
  purple: { bg: '#F1EBFC', bar: '#7C4DDB', ink: '#5B34B0' },
  gray: { bg: '#F1F4F8', bar: '#8291A1', ink: '#5D6B7A' },
};
const toneOf = (ev) => ev.type === 'group' ? TONE.teal
  : ev.kind === 'assessment' ? TONE.purple
  : ['individual', 'family', 'case_mgmt'].includes(ev.kind) ? TONE.blue : TONE.gray;

const card = { background: T.surface, border: '1px solid ' + T.b200, borderRadius: 11 };
const label = { fontSize: 11.5, color: T.inkSoft, fontWeight: 500 };
const inp = { font: 'inherit', fontSize: 13, padding: '7px 9px', borderRadius: 8, border: '1px solid ' + T.b300, color: T.ink, background: '#fff' };
const btn = (primary) => ({ font: 'inherit', fontSize: 12.5, fontWeight: 500, padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
  border: '1px solid ' + (primary ? '#0878E8' : T.b300), background: primary ? '#0878E8' : '#fff', color: primary ? '#fff' : T.ink });
const seg = (on) => ({ font: 'inherit', fontSize: 12.5, fontWeight: 500, padding: '6px 14px', cursor: 'pointer', border: 'none',
  background: on ? '#0878E8' : '#fff', color: on ? '#fff' : T.ink });

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
const today = () => ymd(new Date());
const addDays = (ds, n) => { const d = new Date(ds + 'T00:00:00'); d.setDate(d.getDate() + n); return ymd(d); };
const mondayOf = (ds) => { const d = new Date(ds + 'T00:00:00'); const day = d.getDay() || 7; return addDays(ds, -(day - 1)); };
const dowIdx = (ds) => (new Date(ds + 'T00:00:00').getDay() || 7) - 1;
const toMin = (t) => { const p = String(t || '0:0').split(':').map(Number); return (p[0] || 0) * 60 + (p[1] || 0); };
const hhmm = (m) => pad(Math.floor(m / 60)) + ':' + pad(m % 60);
const fmt12 = (m) => { const h = Math.floor(m / 60), mm = m % 60; return (h % 12 === 0 ? 12 : h % 12) + ':' + pad(mm) + (h >= 12 ? ' PM' : ' AM'); };
const fmtHour = (m) => { const h = Math.floor(m / 60); return (h % 12 === 0 ? 12 : h % 12) + (h >= 12 ? ' PM' : ' AM'); };
const fmtDay = (ds) => new Date(ds + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtMD = (ds) => new Date(ds + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
const initials = (name) => String(name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
const errText = (e) => (e.response && e.response.data && e.response.data.error) || e.message || 'Something went wrong';

// Side-by-side layout for events that overlap in one day.
function withLanes(events) {
  const sorted = events.slice().sort((a, b) => a.start - b.start || a.end - b.end);
  const ends = [];
  sorted.forEach(ev => {
    let lane = ends.findIndex(e => e <= ev.start);
    if (lane === -1) { lane = ends.length; ends.push(0); }
    ends[lane] = ev.end; ev.lane = lane;
  });
  sorted.forEach(ev => { ev.lanes = ends.length || 1; });
  return sorted;
}

export default function ClinicalCalendar({ user }) {
  const [facs, setFacs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [facilityId, setFacilityId] = useState('');
  const [isSup, setIsSup] = useState(false);
  const [me, setMe] = useState((user && user.id) || '');
  const [clinicians, setClinicians] = useState([]);
  const [who, setWho] = useState('');
  const [monday, setMonday] = useState(mondayOf(today()));
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [mode, setMode] = useState('week');
  const [drawer, setDrawer] = useState(false);
  const [narrow, setNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 900);
  const [dayIdx, setDayIdx] = useState(() => dowIdx(today()));

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    axios.get(API + '/api/clinical-calendar/facilities').then(r => {
      const list = r.data.facilities || [];
      setFacs(list); setIsSup(!!r.data.is_supervisor);
      if (r.data.me) setMe(r.data.me);
      if (list.length) setFacilityId(list[0].id);
      setLoaded(true);
    }).catch(e => { setErr(errText(e)); setLoaded(true); });
  }, []);

  useEffect(() => {
    if (!facilityId || !isSup) { setClinicians([]); return; }
    axios.get(API + '/api/clinical-calendar/clinicians', { params: { facility_id: facilityId } })
      .then(r => setClinicians(r.data.clinicians || [])).catch(() => setClinicians([]));
  }, [facilityId, isSup]);

  const loadWeek = useCallback(async () => {
    if (!facilityId) return;
    setErr('');
    try {
      const r = await axios.get(API + '/api/clinical-calendar/week',
        { params: { facility_id: facilityId, week_of: monday, user_id: who || undefined } });
      setData(r.data);
    } catch (e) { setData(null); setErr(errText(e)); }
  }, [facilityId, monday, who]);
  useEffect(() => { loadWeek(); }, [loadWeek]);

  if (!loaded) return <div style={{ padding: 20, color: T.inkSoft, fontSize: 13 }}>Loading the calendar</div>;
  if (!facs.length) return (
    <div style={{ padding: 20, fontFamily: "'Inter', system-ui, sans-serif", color: T.ink }}>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Clinical Calendar</div>
      <div style={{ ...card, padding: 16, fontSize: 13, color: T.inkSoft }}>
        {err || 'The Clinical Calendar is not turned on for your facility yet. Ask an administrator to turn it on.'}
      </div>
    </div>
  );

  const days = [0, 1, 2, 3, 4, 5, 6].map(i => addDays(monday, i));
  const oneDay = narrow || mode === 'day';
  const shown = oneDay ? [days[Math.min(dayIdx, 6)]] : days;
  const hours = (data && data.hours) || { group: 0, individual: 0, other: 0, total: 0 };
  const unscheduled = (data && data.unscheduled) || [];
  const caseload = (data && data.caseload) || [];
  const authsExp = (data && data.auths_expiring) || [];
  const whoRow = data && data.who;
  const whoName = whoRow ? whoRow.name + (whoRow.credentials ? ', ' + whoRow.credentials : '') : '';
  const gridH = (END_H - START_H) * HOUR_PX;
  const defaultDay = () => (oneDay ? days[Math.min(dayIdx, 6)] : (days.includes(today()) ? today() : days[0]));

  const eventsFor = (d) => {
    if (!data) return [];
    const g = (data.groups || []).filter(x => x.d === d).map((x, i) => {
      const s = toMin(x.start_time); const n = Number(x.client_count) || 0;
      return { type: 'group', kind: 'group', key: 'g' + (x.id || i + x.start_time), start: s, end: s + (Number(x.duration_minutes) || 60),
        title: x.topic || 'Group', sub: n + ' client' + (n === 1 ? '' : 's') };
    });
    const a = (data.appointments || []).filter(x => x.d === d).map(x => {
      const s = toMin(x.start_time);
      return { type: 'appt', key: 'a' + x.id, appt: x, kind: x.kind, start: s, end: s + (Number(x.duration_minutes) || 60),
        title: x.patient_name || x.title || KIND_LABEL[x.kind],
        sub: [x.patient_name && x.title ? x.title : '', x.loc || '', x.status === 'done' ? 'Done' : ''].filter(Boolean).join(', ') };
    });
    return withLanes(g.concat(a));
  };

  const openNew = (d, startMin, episodeId, kind) => setForm({ id: null, kind: kind || 'individual', episode_id: episodeId || '', title: '', date: d,
    start: hhmm(startMin), end: hhmm(Math.min(startMin + 60, 23 * 60 + 55)), repeat: 0, notes: '', status: 'scheduled', series_id: null });
  const openEdit = (a) => { const s = toMin(a.start_time); setForm({ id: a.id, kind: a.kind, episode_id: a.episode_id || '', title: a.title || '',
    date: a.d, start: hhmm(s), end: hhmm(s + (Number(a.duration_minutes) || 60)), repeat: 0, notes: a.notes || '', status: a.status,
    series_id: a.series_id || null, patient_name: a.patient_name || '' }); };
  const slotFromY = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const slot = Math.max(0, Math.floor(((e.clientY - r.top) / HOUR_PX) * 2));
    return Math.min(START_H * 60 + slot * 30, (END_H - 1) * 60);
  };

  const save = async () => {
    const s = toMin(form.start), e = toMin(form.end);
    if (e <= s) { alert('The end time must be after the start time.'); return; }
    const clash = eventsFor(form.date).filter(ev => !(ev.appt && ev.appt.id === form.id) && ev.start < e && ev.end > s);
    if (clash.length && !window.confirm('This overlaps ' + clash.map(c => c.title + ' (' + fmt12(c.start) + ')').join(' and ') + '.\n\nSave it anyway?')) return;
    setBusy(true);
    try {
      const body = { facility_id: facilityId, user_id: who || undefined, kind: form.kind, title: form.title,
        episode_id: CLIENT_KINDS.includes(form.kind) ? (form.episode_id || null) : null,
        appt_date: form.date, start_time: form.start, duration_minutes: e - s, notes: form.notes };
      if (form.id) await axios.put(API + '/api/clinical-calendar/appointments/' + form.id, body);
      else await axios.post(API + '/api/clinical-calendar/appointments', { ...body, repeat_weeks: Number(form.repeat) || 0 });
      setForm(null); await loadWeek();
    } catch (e2) { alert('Could not save: ' + errText(e2)); }
    setBusy(false);
  };
  const setStatus = async (status) => {
    setBusy(true);
    try { await axios.put(API + '/api/clinical-calendar/appointments/' + form.id, { status }); setForm(null); await loadWeek(); }
    catch (e2) { alert('Could not save: ' + errText(e2)); }
    setBusy(false);
  };
  const cancelAppt = async () => {
    let series = false;
    if (form.series_id) series = window.confirm('This repeats weekly.\n\nOK = cancel this one and all later ones.\nCancel = cancel only this one.');
    else if (!window.confirm('Cancel this appointment?')) return;
    setBusy(true);
    try { await axios.delete(API + '/api/clinical-calendar/appointments/' + form.id + (series ? '?series=1' : '')); setForm(null); await loadWeek(); }
    catch (e2) { alert('Could not cancel: ' + errText(e2)); }
    setBusy(false);
  };

  const metric = (value, name) => (
    <div style={{ ...card, padding: '10px 16px', flex: '1 1 110px', minWidth: 110 }}>
      <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.15 }}>{value}h</div>
      <div style={{ fontSize: 12, color: T.inkSoft }}>{name}</div>
    </div>
  );
  const avatar = (name, tone) => (
    <span style={{ flex: '0 0 30px', width: 30, height: 30, borderRadius: 15, background: tone.bg, color: tone.ink, fontSize: 11.5, fontWeight: 600,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{initials(name)}</span>
  );
  const insights = [];
  if (unscheduled.length) insights.push({ key: 'ind', text: unscheduled.length + ' individual session' + (unscheduled.length === 1 ? '' : 's') + ' still need scheduling' });
  if (authsExp.length) insights.push({ key: 'auth', text: authsExp.length + ' authorization' + (authsExp.length === 1 ? ' expires' : 's expire') + ' this week' });

  const drawerBody = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid ' + T.b200 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Needs scheduling</div>
        <span style={{ flex: 1 }} />
        <button style={{ ...btn(false), padding: '3px 9px' }} onClick={() => setDrawer(false)}>Close</button>
      </div>
      <div style={{ padding: '10px 14px' }}>
        <div style={{ ...label, marginBottom: 6 }}>{'Individual session this week (' + unscheduled.length + ')'}</div>
        {unscheduled.length === 0 && <div style={{ fontSize: 12.5, color: T.inkSoft, marginBottom: 8 }}>Every client on this caseload has one scheduled.</div>}
        {unscheduled.map(c => (
          <div key={c.episode_id} draggable={!narrow}
            onDragStart={e => { e.dataTransfer.setData('text/plain', String(c.episode_id)); e.dataTransfer.effectAllowed = 'copy'; }}
            onClick={() => { openNew(defaultDay(), 9 * 60, c.episode_id); if (narrow) setDrawer(false); }}
            title={narrow ? 'Tap to schedule' : 'Click to schedule, or drag onto a time'}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 6px', borderRadius: 8, cursor: 'pointer', borderBottom: '1px solid ' + T.b100 }}>
            {avatar(c.patient_name, TONE.blue)}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.patient_name || 'Client'}</div>
              <div style={{ fontSize: 11.5, color: T.inkSoft }}>{'Individual session needed' + (c.loc ? ', ' + c.loc : '')}</div>
            </div>
          </div>
        ))}
        {authsExp.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ ...label, marginBottom: 6 }}>{'Authorization expires this week (' + authsExp.length + ')'}</div>
            {authsExp.map(c => (
              <div key={c.episode_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 6px', borderBottom: '1px solid ' + T.b100 }}>
                {avatar(c.patient_name, { bg: '#FFF4DC', ink: '#9A6700' })}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{c.patient_name || 'Client'}</div>
                  <div style={{ fontSize: 11.5, color: T.inkSoft }}>{'Authorized through ' + fmtMD(c.auth_thru) + (c.loc ? ', ' + c.loc : '')}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ padding: 20, fontFamily: "'Inter', system-ui, sans-serif", color: T.ink }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>Clinical Calendar</div>
          <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 2 }}>{(whoName ? whoName + ', ' : '') + fmtDay(days[0]) + ' to ' + fmtDay(days[6])}</div>
        </div>
        <span style={{ flex: 1 }} />
        {facs.length > 1 && (
          <select style={inp} value={facilityId} onChange={e => { setFacilityId(e.target.value); setWho(''); }}>
            {facs.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
          </select>
        )}
        {isSup && (
          <select style={inp} value={who} onChange={e => setWho(e.target.value)}>
            <option value="">My calendar</option>
            {clinicians.filter(c => c.id !== me).map(c => (
              <option key={c.id} value={c.id}>{c.first_name + ' ' + c.last_name + (c.credentials ? ', ' + c.credentials : '')}</option>
            ))}
          </select>
        )}
        {!narrow && (
          <span style={{ display: 'inline-flex', border: '1px solid ' + T.b300, borderRadius: 8, overflow: 'hidden' }}>
            <button style={seg(mode === 'week')} onClick={() => setMode('week')}>Week</button>
            <button style={seg(mode === 'day')} onClick={() => setMode('day')}>Day</button>
          </span>
        )}
        <button style={btn(false)} onClick={() => setMonday(addDays(monday, -7))}>Prev</button>
        <button style={btn(false)} onClick={() => setMonday(addDays(monday, 7))}>Next</button>
        <button style={btn(false)} onClick={() => { setMonday(mondayOf(today())); setDayIdx(dowIdx(today())); }}>Today</button>
        <button style={btn(true)} onClick={() => openNew(defaultDay(), 9 * 60)}>Add appointment</button>
      </div>

      {err && <div style={{ ...card, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#B42318', borderColor: '#F3C2BD' }}>{err}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        {metric(hours.group, 'Groups')}{metric(hours.individual, 'Individual')}{metric(hours.total, 'Total')}
        <button onClick={() => setDrawer(true)}
          style={{ ...card, flex: '2 1 260px', minWidth: 240, padding: '10px 16px', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: T.ink,
            background: unscheduled.length ? '#FFF8E9' : T.surface, borderColor: unscheduled.length ? '#F2D9A0' : T.b200,
            display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.15 }}>{unscheduled.length}</div>
          <div style={{ fontSize: 12.5, flex: 1 }}>
            {unscheduled.length === 0 ? 'Every client has an individual session this week'
              : 'client' + (unscheduled.length === 1 ? '' : 's') + ' still need' + (unscheduled.length === 1 ? 's' : '') + ' an individual session this week'}
          </div>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0878E8', whiteSpace: 'nowrap' }}>View clients</span>
        </button>
      </div>

      {insights.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '8px 14px', marginBottom: 12, borderRadius: 8,
          background: '#F2F8FF', border: '1px solid #D5E7FB', fontSize: 12.5 }}>
          <span style={{ fontWeight: 600, color: '#0B5CB8' }}>TCIO insights</span>
          {insights.map(i => (
            <button key={i.key} onClick={() => setDrawer(true)}
              style={{ font: 'inherit', fontSize: 12.5, background: 'none', border: 'none', padding: 0, color: T.ink, cursor: 'pointer' }}>{i.text}</button>
          ))}
        </div>
      )}

      {oneDay && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, overflowX: 'auto' }}>
          {days.map((d, i) => (
            <button key={d} style={{ ...btn(i === dayIdx), flex: '0 0 auto' }} onClick={() => setDayIdx(i)}>{DOW[i] + ' ' + fmtMD(d)}</button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ ...card, overflow: 'hidden', flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex' }}>
            <div style={{ flex: '0 0 56px' }}>
              <div style={{ height: 36, borderBottom: '1px solid ' + T.b200 }} />
              {Array.from({ length: END_H - START_H }, (_, i) => (
                <div key={i} style={{ height: HOUR_PX, fontSize: 11, color: T.inkSoft, textAlign: 'right', padding: '2px 8px 0 0', boxSizing: 'border-box' }}>{fmtHour((START_H + i) * 60)}</div>
              ))}
            </div>
            {shown.map(d => {
              const evs = eventsFor(d);
              const isToday = d === today();
              return (
                <div key={d} style={{ flex: 1, minWidth: 0, borderLeft: '1px solid ' + T.b200 }}>
                  <div style={{ height: 36, borderBottom: '1px solid ' + T.b200, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12.5, fontWeight: isToday ? 600 : 500, color: isToday ? '#0878E8' : T.ink, background: isToday ? '#EAF3FE' : 'transparent' }}>
                    {DOW[days.indexOf(d)] + ' ' + fmtMD(d)}
                  </div>
                  <div
                    onClick={e => openNew(d, slotFromY(e))}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                    onDrop={e => { e.preventDefault(); const ep = e.dataTransfer.getData('text/plain'); if (ep) openNew(d, slotFromY(e), ep); }}
                    title="Click an open time to add an appointment"
                    style={{ position: 'relative', height: gridH, cursor: 'pointer', background: isToday ? '#FAFCFF' : 'transparent',
                      backgroundImage: 'repeating-linear-gradient(to bottom, ' + T.b200 + ' 0, ' + T.b200 + ' 1px, transparent 1px, transparent ' + HOUR_PX + 'px)' }}>
                    {evs.map(ev => {
                      const tone = toneOf(ev);
                      const top = Math.max(0, ((ev.start - START_H * 60) / 60) * HOUR_PX);
                      const h = Math.max(22, Math.min(gridH - top, ((ev.end - ev.start) / 60) * HOUR_PX) - 3);
                      const w = 100 / ev.lanes;
                      const range = fmt12(ev.start) + ' - ' + fmt12(ev.end);
                      return (
                        <div key={ev.key}
                          onClick={e => { e.stopPropagation(); if (ev.type === 'appt') openEdit(ev.appt); }}
                          title={SHORT[ev.kind] + ': ' + ev.title + ', ' + range + (ev.sub ? ', ' + ev.sub : '') + (ev.type === 'group' ? ' (edit groups on the EMR schedule)' : '')}
                          style={{ position: 'absolute', top: top + 1, left: 'calc(' + (ev.lane * w) + '% + 3px)', width: 'calc(' + w + '% - 6px)', height: h,
                            background: tone.bg, borderLeft: '3px solid ' + tone.bar, borderRadius: 6, padding: '4px 7px', overflow: 'hidden',
                            fontSize: 11.5, lineHeight: 1.3, cursor: ev.type === 'appt' ? 'pointer' : 'default', boxSizing: 'border-box',
                            opacity: ev.appt && ev.appt.status === 'done' ? 0.6 : 1 }}>
                          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: tone.ink }}>{SHORT[ev.kind]}</div>
                          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</div>
                          {h >= 54 && <div style={{ color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{range}</div>}
                          {h >= 72 && ev.sub && <div style={{ color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.sub}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {drawer && !narrow && <div style={{ ...card, flex: '0 0 280px', width: 280, maxHeight: gridH + 36, overflowY: 'auto' }}>{drawerBody}</div>}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: T.inkSoft, marginTop: 10 }}>
        {[['Individual', TONE.blue], ['Group', TONE.teal], ['Assessment', TONE.purple], ['Admin or unavailable', TONE.gray]].map(([n, t]) => (
          <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 9, height: 9, borderRadius: 5, background: t.bar, display: 'inline-block' }} />{n}
          </span>
        ))}
        <span style={{ flex: 1 }} />
        <span>Groups come from the EMR group schedule. Click any open time to add an appointment.</span>
      </div>

      {drawer && narrow && (
        <div onClick={() => setDrawer(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(7,26,45,0.45)', zIndex: 290, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxHeight: '75vh', overflowY: 'auto', borderRadius: '14px 14px 0 0' }}>{drawerBody}</div>
        </div>
      )}

      {form && (
        <div onClick={() => !busy && setForm(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(7,26,45,0.45)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: 430, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(7,26,45,0.25)' }}>
            <div style={{ padding: '13px 18px', background: T.b100, borderBottom: '1px solid ' + T.b200, fontWeight: 600, fontSize: 14 }}>
              {form.id ? 'Change appointment' : 'Schedule appointment'}
            </div>
            <div style={{ padding: 18, display: 'grid', gap: 12 }}>
              {!form.id && CLIENT_KINDS.includes(form.kind) && unscheduled.length > 0 && (
                <div>
                  <div style={{ ...label, marginBottom: 6 }}>Suggested</div>
                  <div style={{ border: '1px solid ' + T.b200, borderRadius: 8, overflow: 'hidden' }}>
                    {unscheduled.slice(0, 5).map((c, i) => {
                      const on = form.episode_id === c.episode_id;
                      return (
                        <div key={c.episode_id} onClick={() => setForm({ ...form, episode_id: c.episode_id, kind: 'individual' })}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', cursor: 'pointer',
                            background: on ? '#EAF3FE' : '#fff', borderTop: i ? '1px solid ' + T.b100 : 'none' }}>
                          {avatar(c.patient_name, TONE.blue)}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{c.patient_name || 'Client'}</div>
                            <div style={{ fontSize: 11.5, color: T.inkSoft }}>Individual session needed this week</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <div style={{ ...label, marginBottom: 4 }}>Appointment type</div>
                <select style={{ ...inp, width: '100%' }} value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>
                  {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              {CLIENT_KINDS.includes(form.kind) && (
                <div>
                  <div style={{ ...label, marginBottom: 4 }}>{form.id ? 'Client' : 'All clients'}</div>
                  <select style={{ ...inp, width: '100%' }} value={form.episode_id} onChange={e => setForm({ ...form, episode_id: e.target.value })}>
                    <option value="">No client selected</option>
                    {form.episode_id && !caseload.some(c => c.episode_id === form.episode_id) &&
                      <option value={form.episode_id}>{form.patient_name || 'Current client'}</option>}
                    {caseload.map(c => <option key={c.episode_id} value={c.episode_id}>{(c.patient_name || 'Client') + (c.loc ? ' (' + c.loc + ')' : '')}</option>)}
                  </select>
                  {caseload.length === 0 && <div style={{ fontSize: 11.5, color: T.inkSoft, marginTop: 4 }}>No clients are assigned to this clinician on the bed board yet.</div>}
                </div>
              )}
              <div>
                <div style={{ ...label, marginBottom: 4 }}>Title (optional)</div>
                <input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.title} placeholder={KIND_LABEL[form.kind]}
                  onChange={e => setForm({ ...form, title: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 140px' }}>
                  <div style={{ ...label, marginBottom: 4 }}>Date</div>
                  <input type="date" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                </div>
                <div style={{ flex: '1 1 100px' }}>
                  <div style={{ ...label, marginBottom: 4 }}>Start</div>
                  <input type="time" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} />
                </div>
                <div style={{ flex: '1 1 100px' }}>
                  <div style={{ ...label, marginBottom: 4 }}>End</div>
                  <input type="time" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={form.end} onChange={e => setForm({ ...form, end: e.target.value })} />
                </div>
              </div>
              {!form.id && (
                <div>
                  <div style={{ ...label, marginBottom: 4 }}>Repeat</div>
                  <select style={{ ...inp, width: '100%' }} value={form.repeat} onChange={e => setForm({ ...form, repeat: Number(e.target.value) })}>
                    <option value={0}>Does not repeat</option>
                    <option value={3}>Weekly for 4 weeks</option>
                    <option value={7}>Weekly for 8 weeks</option>
                    <option value={11}>Weekly for 12 weeks</option>
                  </select>
                </div>
              )}
              <div>
                <div style={{ ...label, marginBottom: 4 }}>Note (optional)</div>
                <textarea style={{ ...inp, width: '100%', minHeight: 54, resize: 'vertical', boxSizing: 'border-box' }} value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button style={btn(true)} disabled={busy} onClick={save}>{busy ? 'Saving' : (form.id ? 'Save changes' : 'Create appointment')}</button>
                {form.id && form.status !== 'done' && <button style={btn(false)} disabled={busy} onClick={() => setStatus('done')}>Mark done</button>}
                {form.id && form.status === 'done' && <button style={btn(false)} disabled={busy} onClick={() => setStatus('scheduled')}>Mark not done</button>}
                {form.id && <button style={{ ...btn(false), color: '#B42318' }} disabled={busy} onClick={cancelAppt}>Cancel appointment</button>}
                <span style={{ flex: 1 }} />
                <button style={btn(false)} disabled={busy} onClick={() => setForm(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
